import React, { useState, useRef, useEffect } from "react";
import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import moment from "moment";

import ReportFilter from "../ReportsGps/ReportFilter";
import ReportLayoutPage from "../ReportsGps/ReportLayoutPage";
import LoadingComponent from "../CommonComponents/loadinComponent";
import { useAttributePreference } from "../CommonComponents/preferences";
import DataTable from "../UserManagement/SubUserManagement/Datatable";

import { FMSReportAction } from "../../actions/FMSReportAction";
import { PermissionAction } from "../../actions/permission";
import {
  useFuelEventReview, ReviewStatusPill, ReviewActionButtons,
  BulkReviewBar, ReviewDialogs,
} from "./FuelReviewShared";
import "../UserManagement/SubUserManagement/subUser.css";

// Safe numeric formatter
function fmtNum(val, decimals = 2) {
  if (val === null || val === undefined || val === "") return "—";
  const n = Number(val);
  return Number.isNaN(n) ? String(val) : n.toFixed(decimals);
}

// Accepts epoch ms, ISO string or an already formatted string
function fmtDateTime(val) {
  if (val === null || val === undefined || val === "") return "—";
  const m = moment(typeof val === "string" && /^\d+$/.test(val) ? Number(val) : val);
  return m.isValid() ? m.format("DD-MM-YYYY HH:mm:ss") : String(val);
}

// ─────────────────────────────────────────────────────────────────────────────
// Cell — base text style for every body cell
// ─────────────────────────────────────────────────────────────────────────────
const cellTextStyle = { color: "#374151", fontSize: 13 };

const Cell = ({ children, style }) => (
  <span style={{ ...cellTextStyle, ...style }}>{children}</span>
);

// ─────────────────────────────────────────────────────────────────────────────
// COLUMN META — id/label pairs (toggle dropdown, localStorage defaults,
// export headers).
// ─────────────────────────────────────────────────────────────────────────────
const COLUMN_META = [
  { id: "select", label: "", noExport: true },
  { id: "sno", label: "S.No" },
  { id: "vehicleNo", label: "Vehicle No" },
  { id: "vehicleDetail", label: "Vehicle Detail" },
  { id: "dateTime", label: "Date & Time" },
  { id: "address", label: "Address" },
  { id: "area", label: "Area" },
  { id: "beforeDroppage", label: "Before Droppage(Ltr.)" },
  { id: "droppage", label: "Droppage(Ltr.)" },
  { id: "afterDroppage", label: "After Droppage(Ltr.)" },
  { id: "reviewStatus", label: "Review Status" },
  { id: "reviewRemarks", label: "Review Remarks" },
  { id: "reviewActions", label: "Action", noExport: true },
  { id: "viewFuelGraph", label: "View Fuel Graph" },
];

const colSettingsKey = "fuel_droppage_report_col_settings";

const defaultVisibleColumns = COLUMN_META.reduce((acc, col) => {
  acc[col.id] = true;
  return acc;
}, {});

function buildColumns({ pageNo, pageSize, reportDates, review }) {
  const renderers = {
    select: (row) => (
      <input
        type="checkbox"
        disabled={row.reviewStatus !== "UNCHECKED"}
        checked={review.selectedIds.includes(row._id)}
        onChange={() => review.toggleOne(row._id)}
        onClick={(e) => e.stopPropagation()}
      />
    ),
    sno: (_row, i) => <Cell>{i + 1 + (pageNo || 0) * (pageSize || 0)}</Cell>,
    vehicleNo: (row) => <Cell style={{ fontWeight: 600 }}>{row.vehicleNo}</Cell>,
    vehicleDetail: (row) => <Cell>{row.vehicleDetail}</Cell>,
    dateTime: (row) => <Cell style={{ whiteSpace: "nowrap" }}>{row.dateTime}</Cell>,
    address: (row) => <Cell>{row.address}</Cell>,
    area: (row) => <Cell>{row.area}</Cell>,
    beforeDroppage: (row) => <Cell>{row.beforeDroppage}</Cell>,
    droppage: (row) => (
      <Cell style={{ fontWeight: 600, color: "#b91c1c" }}>{row.droppage}</Cell>
    ),
    afterDroppage: (row) => <Cell>{row.afterDroppage}</Cell>,
    reviewStatus: (row) => (
      <span title={row.reviewedBy ? `By ${row.reviewedBy} on ${row.reviewedAt}` : ""}>
        <ReviewStatusPill status={row.reviewStatus} />
      </span>
    ),
    reviewRemarks: (row) => <Cell style={{ maxWidth: 180 }}>{row.reviewRemarks || "—"}</Cell>,
    reviewActions: (row) => (
      <ReviewActionButtons row={row} onAction={(a) => review.openDialog(a, [row])} />
    ),
    viewFuelGraph: (row) => (
      <div
        onClick={(e) => {
          e.stopPropagation();
          localStorage.setItem("vehicleNumber", JSON.stringify(row.vehicleNo || ""));

          const eventMoment = row.eventTime ? moment(row.eventTime) : null;
          if (eventMoment && eventMoment.isValid()) {
            // 15 min before and after the droppage event
            localStorage.setItem(
              "historyFromDate",
              eventMoment.clone().subtract(15, "minutes").toDate(),
            );
            localStorage.setItem(
              "historyToDate",
              eventMoment.clone().add(15, "minutes").toDate(),
            );
          } else {
            // fallback to the report's date range
            if (reportDates.from) {
              localStorage.setItem("historyFromDate", new Date(reportDates.from));
            }
            if (reportDates.to) {
              localStorage.setItem("historyToDate", new Date(reportDates.to));
            }
          }

          localStorage.setItem("historyOpenPanel", "fuel");
          window.open(`/history/${row.gpsRefId}`, "_blank");
        }}
        style={{ cursor: "pointer", display: "inline-flex" }}
      >
        <span style={{ ...cellTextStyle, display: "inline-flex", alignItems: "center", padding: "3px 12px", borderRadius: 999, fontWeight: 700, background: "#082D6B", color: "#ffffff", }}        >          View        </span>
      </div>
    ),
  };

  return COLUMN_META.map((meta) => ({ ...meta, render: renderers[meta.id] }));
}

// ─────────────────────────────────────────────────────────────────────────────
// FILTER
// ─────────────────────────────────────────────────────────────────────────────
const Filter = ({ onSearch, setReportType, extraActions, exportData }) => {
  const handleSubmit = (deviceId, from, to) => {
    const fromDate = moment(from);
    const toDate = moment(to);

    if (toDate.isBefore(fromDate)) {
      alert("To Date cannot be less than From Date");
      return;
    }
    if (toDate.diff(fromDate, "months", true) > 1) {
      alert("Date difference should not be more than 1 month");
      return;
    }

    onSearch(deviceId, from, to);
  };

  const labelHeader = COLUMN_META.filter((c) => !c.noExport).map((c) => c.label);

  return (
    <ReportFilter
      handleSubmit={handleSubmit}
      setReportType={setReportType}
      reportType="fmsFuelDroppage"
      exportData={exportData}
      labelHeader={labelHeader}
      extraActions={extraActions}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────
const FuelDroppageReport = (props) => {
  const { setReportType } = props;
  const review = useFuelEventReview({
    onDone: () => refreshCurrentPage(),
    eventLabel: "Fuel Drop",
    qtyKey: "droppage",
  });
  useAttributePreference("distanceUnit");

  const [items, setItems] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [reportDates, setReportDates] = useState({ from: null, to: null });

  // ── Pagination (server-side) ──
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const lastFilterRef = useRef({ deviceIds: [], from: null, to: null });

  const mapDropsToRows = (drops) =>
    drops.map((entry, idx) => ({
      _idx: idx,
      _id: entry.id,
      gpsRefId: entry.deviceId || entry.vehicleId,
      eventTime: entry.eventTime,
      vehicleNo: entry.vehicleNo || "—",
      vehicleDetail: entry.vehicleDetail || "—",
      dateTime: fmtDateTime(entry.eventTime),
      address:
        entry.latitude != null && entry.longitude != null
          ? `${Number(entry.latitude).toFixed(5)}, ${Number(entry.longitude).toFixed(5)}`
          : "—",
      area: entry.area || "—",

      beforeDroppage: fmtNum(entry.beforeL),
      droppage: fmtNum(entry.quantityL),
      afterDroppage: fmtNum(entry.afterL),

      // review
      reviewStatus: entry.status || "UNCHECKED",
      reviewRemarks: entry.reviewRemarks || "",
      reviewedBy: entry.reviewedBy || "",
      reviewedAt: entry.reviewedAt ? fmtDateTime(entry.reviewedAt) : "",
    }));


  const fetchReport = async (deviceIds, from, to, page = 0, size = pageSize) => {
    review.clearSelection();
    setLoading(true);
    try {
      const response = await FMSReportAction.fetchFmsFuelDroppageReport({
        fromDate: from,
        toDate: to,
        vehicleIds: deviceIds,
        page,
        size,
      });
      setReportDates({ from, to });

      const drops = response?.rows?.content || [];

      setTotalCount(response?.rows?.totalElements ?? drops.length);
      setPageNo(page);
      setPageSize(size);

      const rows = mapDropsToRows(drops);
      setItems(rows);
      setExportData(rows);
    } catch (err) {
      console.error("Error fetching FMS fuel droppage report:", err);
      setItems([]);
      setExportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (deviceId, from, to) => {
    let deviceIds = [];
    if (Array.isArray(deviceId)) {
      deviceIds = deviceId.map((v) => v);
    } else if (deviceId) {
      deviceIds = [deviceId];
    }
    lastFilterRef.current = { deviceIds, from, to };
    fetchReport(deviceIds, from, to, 0, pageSize);
  };

  const handleChangePage = (event, newPage) => {
    const { deviceIds, from, to } = lastFilterRef.current;
    if (!from || !to) return;
    fetchReport(deviceIds, from, to, newPage, pageSize);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    const { deviceIds, from, to } = lastFilterRef.current;
    if (!from || !to) {
      setPageSize(newSize);
      return;
    }
    fetchReport(deviceIds, from, to, 0, newSize);
  };


  const refreshCurrentPage = () => {
    const { deviceIds, from, to } = lastFilterRef.current;
    if (!from || !to) return;
    fetchReport(deviceIds, from, to, pageNo, pageSize);
  };

  // ── Column visibility ──
  const [headerConfigs, setHeaderConfigs] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(colSettingsKey);
    return saved ? { ...defaultVisibleColumns, ...JSON.parse(saved) } : defaultVisibleColumns;
  });
  const [showColSettings, setShowColSettings] = useState(false);
  const colSettingsRef = useRef(null);

  const fetchHeaderVisibility = () => {
    const stored = JSON.parse(localStorage.getItem("data"));
    if (stored && stored.id) {
      PermissionAction.getTableHeadersVisibility(
        "FUEL DROPPAGE REPORT PAGE",
        stored.id,
      ).then((res) => {
        if (res && res.data && Array.isArray(res.data)) {
          setHeaderConfigs(res.data);
          const apiVisibility = {};
          res.data.forEach((item) => {
            apiVisibility[item.headerName] = item.isVisible;
          });
          setVisibleColumns((prev) => ({ ...prev, ...apiVisibility }));
        }
      });
    }
  };

  useEffect(() => {
    fetchHeaderVisibility();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (colSettingsRef.current && !colSettingsRef.current.contains(e.target)) {
        setShowColSettings(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleColumn = async (colId) => {
    const nextVal = !visibleColumns[colId];
    setVisibleColumns((prev) => {
      const next = { ...prev, [colId]: nextVal };
      localStorage.setItem(colSettingsKey, JSON.stringify(next));
      return next;
    });

    const stored = JSON.parse(localStorage.getItem("data"));
    if (stored && stored.id && headerConfigs.length > 0) {
      const updatedConfigs = headerConfigs.map((cfg) =>
        cfg.headerName === colId ? { ...cfg, isVisible: nextVal } : cfg,
      );
      await PermissionAction.updateTableHeadersVisibility(updatedConfigs);
      fetchHeaderVisibility();
    }
  };

  const allColumns = buildColumns({
    pageNo,
    pageSize,
    reportDates,
    review
  });
  const visibleColumnDefs = allColumns.filter((col) => visibleColumns[col.id]);

  return (
    <div className="sub-user-list-root" style={{ position: "relative" }}>
      <LoadingComponent isLoading={isLoading} />
      <ReviewDialogs review={review} />
      <Container style={{ marginTop: "-2rem" }}>
        <ReportLayoutPage
          filter={
            <Filter
              onSearch={handleSearch}
              setReportType={setReportType}
              exportData={exportData}
              extraActions={
                <div className="gf-col-settings-wrap" ref={colSettingsRef}>
                  <button
                    className={`gf-col-settings-btn ${showColSettings ? "active" : ""}`}
                    onClick={() => setShowColSettings(!showColSettings)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      height: 40,
                      padding: "0 16px",
                      border: "none",
                      borderRadius: 10,
                      background: showColSettings
                        ? "linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #334155 60%, #475569 100%)"
                        : "linear-gradient(135deg, #1e293b 0%, #334155 30%, #475569 60%, #64748b 100%)",
                      color: "#ffffff",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      boxShadow: showColSettings
                        ? "0 6px 24px rgba(30, 41, 59, 0.55)"
                        : "0 4px 16px rgba(30, 41, 59, 0.4)",
                      letterSpacing: "0.3px",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: showColSettings ? "translateY(-1px)" : "translateY(0)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #334155 60%, #475569 100%)";
                      e.currentTarget.style.boxShadow = "0 6px 24px rgba(30, 41, 59, 0.55)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      if (!showColSettings) {
                        e.currentTarget.style.background =
                          "linear-gradient(135deg, #1e293b 0%, #334155 30%, #475569 60%, #64748b 100%)";
                        e.currentTarget.style.boxShadow = "0 4px 16px rgba(30, 41, 59, 0.4)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                      <path
                        fillRule="evenodd"
                        d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Columns
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12">
                      <path
                        d="M4 6l4 4 4-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {showColSettings && (
                    <div className="gf-col-dropdown">
                      <div className="gf-col-dropdown-header">Toggle Columns</div>
                      {COLUMN_META.filter((c) => c.id !== "select").map((col) => (
                        <label key={col.id} className="gf-col-dropdown-item">
                          <input
                            type="checkbox"
                            className="gf-col-checkbox"
                            checked={!!visibleColumns[col.id]}
                            onChange={() => toggleColumn(col.id)}
                          />
                          {col.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              }
            />
          }
        >
          {items.length > 0 && <BulkReviewBar review={review} rows={items} />}
          <Paper className="sub-user-list-paper">
            <DataTable
              columns={visibleColumnDefs}
              rows={items}
              rowKey={(row) => row._idx}
              tableId="fuel-droppage-report-table"
              page={pageNo}
              rowsPerPage={pageSize}
              totalCount={totalCount}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]}
              showRowMenu={false}
            />
          </Paper>
        </ReportLayoutPage>
      </Container>
    </div>
  );
};

export default FuelDroppageReport;